import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, Search, ArrowUpRight, ExternalLink, TrendingUp, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { STATUS_BADGE, formatCurrency, formatNumber } from "@/lib/constants";
import { KpiTile, DashHeader } from "@/components/DashboardKit";

export default function ProductDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/products")
      .then((r) => mounted && setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load product dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!data?.products) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.products;
    return data.products.filter((p) =>
      [p.product_name, p.model_no, p.category, p.status].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data, search]);

  if (loading) return <div className="p-6 space-y-4"><Skeleton className="h-10 w-80" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i=><Skeleton key={i} className="h-24" />)}</div><Skeleton className="h-80" /></div>;
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="product-dashboard-page">
      <DashHeader
        eyebrow="Product Dashboard"
        title="Product Catalogue"
        hint="Role-aware view of your product catalogue. Sales and purchase roll-ups are shown only when your role has access to those modules."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        <KpiTile label="Total Products" value={s.total_products} icon={Package} accent="blue" testid="kpi-total" />
        <KpiTile label="Active" value={s.active} sub="In catalogue" icon={Package} accent="emerald" testid="kpi-active" />
        {data.show_sales && <KpiTile label="Sales Value" value={formatCurrency(s.total_sales_value)} icon={TrendingUp} accent="violet" testid="kpi-sales-value" />}
        {data.show_purchase && <KpiTile label="Purchase Value" value={formatCurrency(s.total_purchase_value)} icon={ShoppingCart} accent="amber" testid="kpi-purchase-value" />}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Catalogue</div>
              <div className="text-sm text-slate-500">{filtered.length} of {data.products.length} products</div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input className="pl-9 w-64" placeholder="Search product / model / category" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                {["Product", "Model", "Category", "Current", "Dispatch Pending", "Available", "Status"].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                ))}
                {data.show_sales && <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sales</th>}
                {data.show_purchase && <th className="text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600">Purchase</th>}
                <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-slate-500">No matching products.</td></tr>}
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`product-row-${p.id}`}>
                    <td className="py-2.5 px-3">
                      <div className="font-semibold text-slate-900">{p.product_name || "—"}</div>
                      {p.catalog_link && (
                        <a href={p.catalog_link} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 inline-flex items-center gap-0.5" data-testid={`catalog-link-${p.id}`}>
                          <ExternalLink className="h-3 w-3" /> Catalogue
                        </a>
                      )}
                    </td>
                    <td className="py-2.5 px-3 font-mono">{p.model_no || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-600">{p.category || "—"}</td>
                    <td className="py-2.5 px-3 font-mono">{formatNumber(p.current_stock)}</td>
                    <td className="py-2.5 px-3 font-mono text-amber-700">{formatNumber(p.dispatch_pending)}</td>
                    <td className={`py-2.5 px-3 font-mono font-bold ${p.available > 0 ? "text-emerald-700" : "text-red-600"}`}>{formatNumber(p.available)}</td>
                    <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(p.status || "")}>{p.status || "—"}</Badge></td>
                    {data.show_sales && <td className="py-2.5 px-3 font-mono text-slate-700">{formatCurrency(p.sales_value)}<div className="text-[10px] text-slate-500">{p.sales_orders} orders</div></td>}
                    {data.show_purchase && <td className="py-2.5 px-3 font-mono text-slate-700">{formatCurrency(p.purchase_value)}<div className="text-[10px] text-slate-500">{p.purchase_orders} POs</div></td>}
                    <td className="py-2.5 px-3 text-right">
                      <Link to={`/m/products/${p.id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`product-open-${p.id}`}>
                        Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
